import { useEffect, useState } from 'preact/hooks';
import { combatBus, type CombatState } from '../app/combatBus';
import { appState } from '../app/state';
import { TONE_COLORS, tonesForCharacters } from '../speech/pinyin';

export function CombatHud() {
  const [s, setS] = useState<CombatState>(combatBus.get());
  useEffect(() => combatBus.on(setS), []);

  const settings = appState.get().settings;
  if (s.phase === 'idle' || !s.card) return null;

  const showPinyin = s.showPinyin || (settings?.pinyinReveal === 'always');
  const tones = tonesForCharacters(s.card.hanzi);
  const chars = Array.from(s.card.hanzi);

  const phaseHint =
    s.phase === 'show-card' ? 'hold the wand to speak' :
    s.phase === 'listening' ? 'listening… release to cast' :
    s.phase === 'scoring' ? 'channeling spell…' :
    s.phase === 'feedback' && s.lastOutcome === 'critical' ? '✨ critical hit!' :
    s.phase === 'feedback' && s.lastOutcome === 'hit' ? 'hit!' :
    s.phase === 'feedback' && s.lastOutcome === 'glance' ? 'glancing blow' :
    s.phase === 'feedback' && s.lastOutcome === 'miss' ? 'try again' :
    s.phase === 'feedback' && s.lastOutcome === 'fumble' ? 'monster strikes back!' :
    s.phase === 'scaffold-audio' ? 'listen carefully…' :
    s.phase === 'scaffold-pinyin' ? 'see the pinyin — try again' :
    s.phase === 'scaffold-choral' ? 'say it with the wizard' :
    '';

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', pointerEvents: 'none',
    }}>
      {/* Top bar: HP, exit */}
      <div class="ws-row between" style={{ padding: 12, pointerEvents: 'auto' }}>
        <div class="ws-card" style={{ padding: '6px 12px', background: 'rgba(20,16,42,0.6)' }}>
          {'❤'.repeat(s.heroHp)}<span style={{ opacity: 0.3 }}>{'❤'.repeat(Math.max(0, s.heroMaxHp - s.heroHp))}</span>
        </div>
        <button class="ws-btn ghost" onClick={() => combatBus.dispatch({ kind: 'exit' })}>Pause ⏸</button>
      </div>

      {/* Monster HP at top */}
      <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
        <div style={{ fontSize: 56, lineHeight: 1, filter: s.phase === 'feedback' && s.lastOutcome && s.lastOutcome !== 'miss' && s.lastOutcome !== 'fumble' ? 'brightness(2)' : 'none', transition: 'filter 150ms' }}>
          {s.monsterEmoji}
        </div>
        <div class="ws-muted" style={{ fontSize: 12 }}>{s.monsterName}</div>
        <div style={{ width: 220, margin: '6px auto', height: 8, background: 'rgba(20,16,42,0.6)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.max(0, (s.monsterHp / s.monsterMaxHp) * 100)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, var(--bad), #ff7a4d)',
            transition: 'width 300ms ease',
          }} />
        </div>
      </div>

      {/* Card panel + mic */}
      <div style={{ padding: 16, pointerEvents: 'auto' }}>
        <div class="ws-panel" style={{ margin: '0 auto', maxWidth: 560, padding: 18 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, lineHeight: 1.2, letterSpacing: '0.08em' }}>
              {chars.map((c, i) => (
                <span
                  key={i}
                  style={{
                    borderBottom: `4px solid ${TONE_COLORS[tones[i] ?? 5]}`,
                    paddingBottom: 2,
                    marginRight: 4,
                  }}
                >{c}</span>
              ))}
            </div>
            {showPinyin && <div style={{ fontSize: 22, opacity: 0.85, marginTop: 6 }}>{s.card.pinyin}</div>}
            {s.showEnglish && s.card.english && <div class="ws-muted" style={{ marginTop: 4 }}>{s.card.english}</div>}
            <div class="ws-muted" style={{ marginTop: 8, minHeight: 18 }}>{phaseHint}</div>
          </div>

          {/* score bar after feedback */}
          {s.phase !== 'show-card' && s.phase !== 'listening' && s.phase !== 'scoring' && s.lastScore !== null && (
            <div style={{ marginTop: 10 }}>
              <div style={{ height: 8, background: 'var(--panel-2)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${s.lastScore}%`,
                  background: s.lastScore >= 70 ? 'var(--good)' : s.lastScore >= 40 ? 'var(--warn)' : 'var(--bad)',
                  transition: 'width 250ms ease',
                }} />
              </div>
              {s.lastHeard && (
                <div class="ws-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  heard: <span style={{ fontSize: 16 }}>{s.lastHeard}</span> · score {s.lastScore}
                </div>
              )}
            </div>
          )}

          {/* Mic button */}
          <button
            class="ws-btn big"
            style={{
              display: 'block',
              width: '100%',
              marginTop: 12,
              background: s.phase === 'listening' ? 'var(--bad)' : 'var(--accent)',
              opacity: s.phase === 'scoring' || s.phase === 'feedback' ? 0.6 : 1,
            }}
            disabled={s.phase === 'scoring' || s.phase === 'feedback' || s.phase === 'scaffold-audio'}
            onTouchStart={(e) => { e.preventDefault(); combatBus.dispatch({ kind: 'pressMic' }); }}
            onTouchEnd={(e) => { e.preventDefault(); combatBus.dispatch({ kind: 'releaseMic' }); }}
            onMouseDown={() => combatBus.dispatch({ kind: 'pressMic' })}
            onMouseUp={() => combatBus.dispatch({ kind: 'releaseMic' })}
          >
            {s.phase === 'listening' ? '🎤 Listening…' : s.phase === 'scaffold-choral' ? '🔊 Say it with me' : '🪄 Hold to cast'}
          </button>

          <div class="ws-row between" style={{ marginTop: 8 }}>
            <button class="ws-btn ghost" onClick={() => combatBus.dispatch({ kind: 'replayAudio' })}>🔊 Hear it</button>
            <button class="ws-btn ghost" onClick={() => combatBus.dispatch({ kind: 'sageWhisper' })}>🧙 Sage Whisper</button>
          </div>

          {/* Mic-permission banner. iOS silently produces no speech result if
              mic permission is missing — we must surface this proactively or
              the kid (and parent) thinks the game is broken. */}
          {(s.micState === 'denied' || s.micState === 'unsupported' || s.micState === 'prompt' || s.micState === 'unknown') && (
            <div style={{
              marginTop: 10,
              padding: 10,
              background: s.micState === 'denied' ? 'rgba(214, 99, 77, 0.18)' : 'rgba(245, 239, 255, 0.06)',
              border: `1px solid ${s.micState === 'denied' ? 'rgba(214, 99, 77, 0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 10,
              fontSize: 13,
            }}>
              {s.micState === 'denied' ? (
                <>
                  <strong>🎤 Microphone is blocked.</strong>
                  <div class="ws-muted" style={{ marginTop: 4 }}>
                    Open iPad <strong>Settings → WizardSpeak → Microphone</strong> (or Settings → Safari → Microphone) and allow it, then come back.
                  </div>
                  {s.micLastError && <div class="ws-muted" style={{ fontSize: 11, marginTop: 4 }}>{s.micLastError}</div>}
                </>
              ) : s.micState === 'unsupported' ? (
                <>
                  <strong>This browser can't access the microphone.</strong>
                  <div class="ws-muted" style={{ marginTop: 4 }}>Open in Safari on iPad (not Chrome / Brave / etc.).</div>
                </>
              ) : (
                <div class="ws-row between" style={{ alignItems: 'center' }}>
                  <span>🎤 First time: tap to allow the mic.</span>
                  <button class="ws-btn" style={{ padding: '6px 12px' }} onClick={() => combatBus.dispatch({ kind: 'grantMic' })}>Allow mic</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
